/**
 * IssuerSearch
 *
 * A component that provides a searchable dropdown for selecting bond issuers.
 * Uses the live inventory API to search for issuers dynamically based on user input.
 * Built on top of the shared SearchInput component for consistent UX.
 *
 * @example
 * <IssuerSearch
 *   value={selectedIssuer}
 *   onChange={setSelectedIssuer}
 *   onSelect={(option) => handleIssuerSelection(option)}
 *   placeholder="Search issuers..."
 * />
 */

import type { SearchOption } from '@/shared/components/search-input/types'
import type {
  LiveInventoryItem,
  LiveInventoryReturnValues,
} from '@/types/api/live-inventory'
import type { IssuerSearchProps } from './types'

// biome-ignore lint/correctness/noUnusedImports: Bulk suppression as part of biome migration
import React, { useCallback, useState } from 'react'

import { useAppDispatch } from '@/app/store'
import { useUserRiskGroupId } from '@/hooks/use-user-risk-group-id'
import { getLiveInventoryPost } from '@/legacy-api/data-slice'
import { SearchInput } from '@/shared/components/search-input'

const MAX_RESULTS = 100
const DEBOUNCE_MS = 500
const MIN_SEARCH_LENGTH = 2

function IssuerSearch({
  value,
  onChange,
  onSelect,
  placeholder = 'Search issuers...',
  disabled = false,
  required = false,
  className,
  suppliedRiskGroupId,
  ...props
}: IssuerSearchProps) {
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useAppDispatch()

  const { riskGroupId: defaultRiskGroupId, isLoading: isRiskLoading } =
    useUserRiskGroupId()
  const riskGroupId = suppliedRiskGroupId || defaultRiskGroupId

  const searchIssuers = useCallback(
    async (searchTerm: string): Promise<SearchOption[]> => {
      if (
        !searchTerm ||
        searchTerm.length < MIN_SEARCH_LENGTH ||
        !riskGroupId
      ) {
        return []
      }

      const apiArgs = {
        filters: {
          or: [
            { description: { includes: searchTerm } },
            { description_short: { includes: searchTerm } },
            { issuer: { includes: searchTerm } },
          ],
        },
        returns: ['issuer'] as LiveInventoryReturnValues[], // Only return issuer field for efficiency
        page: 1,
        page_size: MAX_RESULTS * 2, // Return more results to account for duplicates, then filter to MAX_RESULTS below
        risk_group_id: riskGroupId,
      }
      setIsLoading(true)
      // Since we do not have a dedicated endpoint for issuers, we need to use the live inventory endpoint and filter the results
      return dispatch(getLiveInventoryPost.initiate(apiArgs))
        .unwrap()
        .then((res) => {
          const issuersSet = new Set<string>()
          const issuerOptions: SearchOption[] = []
          res.data.forEach((item: LiveInventoryItem) => {
            if (item.issuer && !issuersSet.has(item.issuer)) {
              issuersSet.add(item.issuer)
              issuerOptions.push({
                value: item.issuer,
                label: item.issuer,
                metadata: {
                  issuer: item.issuer,
                },
              })
            }
          })

          // Sort alphabetically and limit results to MAX_RESULTS
          return issuerOptions
            .sort((a, b) => a.label.localeCompare(b.label))
            .slice(0, MAX_RESULTS)
        })
        .finally(() => setIsLoading(false))
    },
    [dispatch, riskGroupId],
  )

  const handleSelect = useCallback(
    (option: SearchOption) => {
      if (onSelect) {
        onSelect(option)
      }
    },
    [onSelect],
  )

  const effectiveIsLoading = isRiskLoading || isLoading
  const effectiveIsDisabled = disabled || isRiskLoading

  return (
    <SearchInput
      value={value}
      onChange={onChange}
      onSearch={searchIssuers}
      onSelect={handleSelect}
      placeholder={placeholder}
      isLoading={effectiveIsLoading}
      disabled={effectiveIsDisabled}
      required={required}
      className={className}
      debounceMs={DEBOUNCE_MS}
      minSearchLength={MIN_SEARCH_LENGTH}
      maxResults={MAX_RESULTS}
      loadingMessage="Searching issuers..."
      emptyMessage="No issuers found"
      errorMessage="Error searching issuers"
      shortSearchMessage={`Type at least ${MIN_SEARCH_LENGTH} characters to search issuers`}
      {...props}
    />
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default IssuerSearch
